import { useState } from 'react'
import styled from 'styled-components/macro'

import { Button, Card, Layout, ProgressBar, Text } from '@loadsmart/loadsmart-ui'

import { screen } from 'legacy/styles/screen'

import { type ConfirmAwardedLaneAction } from '../domain/ConfirmAwardedLaneAction'
import { getConfirmedAwardedLanes, type Proposal } from '../domain/Proposal'
import { getAwardedProgressTitle } from '../presenters/rfpAwardedPresenter'

const StyledCard = styled(Card)`
  padding: 16px;
`

const StyledLayoutStack = styled(Layout.Stack)`
  width: 100%;

  ${screen.lg} {
    width: 60%;
  }

  ${screen.xlg} {
    width: 45%;
  }
`

type AwardedProposalPageProps = {
  awardedProposal: Proposal
  isConfirmingAllLanes: boolean
  isDisabled: boolean
  handleConfirmAll: (action: ConfirmAwardedLaneAction) => void
}

export function AwardedProposalProgress({
  awardedProposal,
  isConfirmingAllLanes,
  isDisabled,
  handleConfirmAll,
}: AwardedProposalPageProps): JSX.Element {
  const [action, setAction] = useState<ConfirmAwardedLaneAction | undefined>()
  const confirmedLanes = getConfirmedAwardedLanes(awardedProposal).length
  const totalLanes = awardedProposal.lanes.length

  const confirmedLanesPercentage = (confirmedLanes / totalLanes) * 100

  function handleConfirm(laneAction: ConfirmAwardedLaneAction) {
    setAction(laneAction)
    handleConfirmAll(laneAction)
  }

  const isAcceptingAll = isConfirmingAllLanes && action === 'accept'
  const isRejectingAll = isConfirmingAllLanes && action === 'deny'

  const disableConfirmation = isConfirmingAllLanes || isDisabled

  return (
    <StyledCard>
      <Layout.Group justify="space-between" align="center">
        <Layout.Stack space="xs">
          <Text variant="heading-md-bold">{getAwardedProgressTitle(awardedProposal)}</Text>
          <Text>Accept or reject it below and start receiving tenders.</Text>
        </Layout.Stack>
        <StyledLayoutStack>
          <Text variant="heading-sm-bold">{`${confirmedLanes} of ${totalLanes} selected`}</Text>
          <ProgressBar status="success" value={confirmedLanesPercentage} rounded showLine />
        </StyledLayoutStack>
        <Layout.Group>
          <Button
            scale="small"
            variant="primary"
            onClick={() => handleConfirm('accept')}
            disabled={disableConfirmation}
            loading={isAcceptingAll}
          >
            Accept all
          </Button>
          <Button
            scale="small"
            variant="secondary"
            onClick={() => handleConfirm('deny')}
            disabled={disableConfirmation}
            loading={isRejectingAll}
          >
            Reject all
          </Button>
        </Layout.Group>
      </Layout.Group>
    </StyledCard>
  )
}
